import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { api, fmtDate, gbp, JOB_STATUS } from "@/admin/api";
import { Badge, Btn, Card, Empty, ErrorMsg, Field, inputCls, Loading, Modal, useAsync } from "@/admin/ui";

export const JobForm = ({ job, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: job?.name || "", phone: job?.phone || "", address: job?.address || "", work_type: job?.work_type || "",
    status: job?.status || "won", value: job?.value ?? "", start_date: job?.start_date || "", end_date: job?.end_date || "", notes: job?.notes || "",
  });
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, value: f.value === "" ? null : Number(f.value) };
    if (!job) { if (!body.start_date) body.start_date = null; if (!body.end_date) body.end_date = null; }
    try {
      onSaved(await api(job ? `/admin/jobs/${job.id}` : "/admin/jobs", { method: job ? "PATCH" : "POST", body }));
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <Modal title={job ? "Edit client & job" : "New client & job"} onClose={onClose} testId="job-modal">
      <form onSubmit={save} className="space-y-4">
        <Field label="Client name"><input data-testid="job-name" required value={f.name} onChange={set("name")} className={inputCls} /></Field>
        <Field label="Phone"><input data-testid="job-phone" type="tel" value={f.phone} onChange={set("phone")} className={inputCls} /></Field>
        <Field label="Address"><input data-testid="job-address" value={f.address} onChange={set("address")} className={inputCls} /></Field>
        <Field label="Type of work"><input data-testid="job-work" value={f.work_type} onChange={set("work_type")} className={inputCls} placeholder="e.g. Loft conversion" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <select data-testid="job-status" value={f.status} onChange={set("status")} className={inputCls}>
              {Object.entries(JOB_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Value (£)"><input data-testid="job-value" type="number" min="0" step="1" inputMode="numeric" value={f.value} onChange={set("value")} className={inputCls} /></Field>
          <Field label="Start date"><input data-testid="job-start" type="date" value={f.start_date} onChange={set("start_date")} className={inputCls} /></Field>
          <Field label="Finish date"><input data-testid="job-end" type="date" value={f.end_date} onChange={set("end_date")} className={inputCls} /></Field>
        </div>
        <Field label="Notes"><textarea data-testid="job-notes" rows={3} value={f.notes} onChange={set("notes")} className={`${inputCls} py-3`} /></Field>
        <p className="text-xs text-body">Revenue counts the job value in the month of its finish date, once that date has passed.</p>
        <ErrorMsg error={error} />
        <Btn type="submit" data-testid="job-save" className="w-full">{job ? "Save changes" : "Create job"}</Btn>
      </form>
    </Modal>
  );
};

export default function Jobs() {
  const { data, error, loading, reload } = useAsync(() => api("/admin/jobs"), []);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const items = (data || []).filter((j) => `${j.name} ${j.address} ${j.work_type} ${j.phone}`.toLowerCase().includes(q.toLowerCase()));
  if (loading) return <Loading />;
  if (error) return <ErrorMsg error={error} />;
  return (
    <div data-testid="jobs-page" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-navy md:text-3xl">Clients &amp; Jobs</h1>
        <Btn onClick={() => setAdding(true)} data-testid="add-job-button" className="min-h-11 px-4"><Plus size={18} /> New</Btn>
      </div>
      <input data-testid="jobs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, address or work" className={inputCls} />
      {items.length === 0 ? (
        <Empty testId="jobs-empty">{data.length === 0 ? "No clients or jobs yet. Mark an enquiry as Won, add a booking, or create one here." : "No matches."}</Empty>
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2">
          {items.map((j) => (
            <li key={j.id}>
              <Link to={`/admin/jobs/${j.id}`} data-testid={`job-card-${j.id}`} className="block">
                <Card className="hover:border-brand">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-lg font-bold text-navy">{j.name}</p>
                      <p className="text-sm text-body">{j.work_type || "—"}{j.address ? ` · ${j.address}` : ""}</p>
                    </div>
                    <Badge map={JOB_STATUS} value={j.status} />
                  </div>
                  <p className="mt-3 text-sm text-body">
                    {j.value ? gbp(j.value) : "No value yet"}
                    {j.start_date ? ` · Starts ${fmtDate(j.start_date)}` : ""}
                    {j.end_date ? ` · Finishes ${fmtDate(j.end_date)}` : ""}
                  </p>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {adding && <JobForm onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />}
    </div>
  );
}
